import {
  CloseCircleOutlined,
  EyeOutlined,
  InfoCircleOutlined,
  LockOutlined,
  PhoneOutlined,
  SettingOutlined,
  TeamOutlined,
  UserOutlined,
} from "@ant-design/icons/lib/icons";
import { Button, Card, Col, Modal, Row, Skeleton, Typography } from "antd";
import { CompanyLogo } from "@bindeleddet/core/components";
import { message } from "@bindeleddet/core/utils";
import { Company, CompanyContact, Event, EventRegistration, IsOpenType, StudentUser } from "@bindeleddet/core/types";
import React, { useCallback, useEffect, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import axios from "axios";

const { Title, Text, Paragraph } = Typography;

interface Props {
  event: Event;
  responsibility_type: number;
  date: string;
  company: Company;
  isLast?: boolean;
  onEventUpdate?: (updatedEvent: Event) => void;
}

interface RegisteredStudentData {
  name: string;
  username: string;
  time_attended?: string;
}

const ResponsibilityBox: React.FC<Props> = ({
  event,
  responsibility_type,
  date,
  company,
  isLast = false,
  onEventUpdate,
}) => {
  const history = useHistory();
  const [companyContact, setCompanyContact] = useState<CompanyContact | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const isHA = responsibility_type === 1;

  useEffect(() => {
    if (!event || !event.company_contact_id || !company) return;
    axios
      .get<CompanyContact>(`/companies/${company.id}/contact-persons/${event.company_contact_id}/`)
      .then(response => {
        setCompanyContact(response.data);
      })
      .catch(() => {
        // Ignore errors, contact might not exist
      });
  }, [event, company]);

  const fetchRegisteredStudents = useCallback(async (): Promise<RegisteredStudentData[]> => {
    if (!event) return [];
    try {
      const registrationsResponse = await axios.get<EventRegistration[]>(`/events/${event.id}/registrations/`);
      const studentPromises = registrationsResponse.data.map(reg =>
        axios.get<StudentUser>(`/students/${reg.student}/`)
      );
      const studentResponses = await Promise.all(studentPromises);

      return registrationsResponse.data.map((reg, index) => {
        const student = studentResponses[index].data;
        return {
          name: `${student.first_name} ${student.last_name}`,
          username: student.username,
          time_attended: reg.time_attended,
        };
      });
    } catch (error) {
      return [];
    }
  }, [event]);

  const handleCloseEvent = () => {
    Modal.confirm({
      title: "Steng arrangementet",
      content: "Er du sikker på at du vil stenge dette arrangementet?",
      okText: "Steng",
      okType: "danger",
      cancelText: "Avbryt",
      onOk: async () => {
        setLoading(true);
        try {
          const response = await axios.put<Event>(`/events/admin-events/${event.id}/`, {
            ...event,
            is_open: IsOpenType.CLOSED,
          });
          const updatedEvent = response.data;
          message("success", "Arrangementet er stengt");
          if (onEventUpdate) {
            onEventUpdate(updatedEvent);
          }
        } catch (error) {
          message("error", "Kunne ikke stenge arrangementet");
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const handleSendNoShow = async () => {
    if (!event || event.is_open !== IsOpenType.CLOSED) {
      message("warning", "Steng arrangementet først for å sende No Show.");
      return;
    }

    try {
      const students = await fetchRegisteredStudents();
      const studentsWithoutAttendance = students.filter(student => !student.time_attended);

      Modal.confirm({
        title: "Send No Show",
        content: (
          <>
            <Paragraph>
              Er du sikker på at du vil sende No Show til alle påmeldte studenter som ikke har registrert oppmøte?
            </Paragraph>
            <Paragraph>
              Følgende studenter vil få en prikk: <br />
              <ul>
                {studentsWithoutAttendance.map(student => (
                  <li key={student.username}>{student.name}</li>
                ))}
                {studentsWithoutAttendance.length === 0 && <li>Ingen studenter</li>}
              </ul>
              <Text italic>
                Hvis noen i listen har registrert oppmøte etter du åpnet denne siden vil de ikke få prikk.
              </Text>
            </Paragraph>
          </>
        ),
        okText: "Send No Show",
        okType: "danger",
        cancelText: "Avbryt",
        onOk: async () => {
          setLoading(true);
          try {
            await axios.post(`/events/${event.id}/send-no-show/`);
            // Fetch updated event to get no_shows_sent status
            const response = await axios.get<Event>(`/events/admin-events/${event.id}/`);
            const updatedEvent = response.data;
            message("success", "No Show er sendt");
            if (onEventUpdate) {
              onEventUpdate(updatedEvent);
            }
          } catch (error) {
            message("error", "Feil under sending av No Show");
          } finally {
            setLoading(false);
          }
        },
      });
    } catch (error) {
      message("error", "Kunne ikke hente studentdata");
    }
  };

  const openEvent = () => {
    return history.push(`/admin/events/${event.id}/`);
  };

  const getResponsibilityType = () => {
    if (responsibility_type === 1) return "HA";
    if (responsibility_type === 2) return "mentor";
    if (responsibility_type === 3) return "MP";
  };

  const getTitle = () => {
    const type = getResponsibilityType();
    const now = new Date();
    const eventStart = new Date(date);

    now.setHours(0, 0, 0, 0); // set both dates to 0 hours so we find days difference, without hours disturbing
    eventStart.setHours(0, 0, 0, 0);

    const difference = eventStart.getTime() - now.getTime();
    const daysDifference = Math.ceil(difference / (1000 * 3600 * 24));
    if (daysDifference > 1) return `Du er ${type} for ${company?.name} om ${daysDifference} dager`;
    if (daysDifference === 1) return `Du er ${type} for ${company?.name} i morgen`;
    if (daysDifference === -1) return `Du var ${type} for ${company?.name} i går`;
    if (daysDifference < -1) return `Du var ${type} for ${company?.name} for ${-daysDifference} dager siden`;
    return `Du er ${type} for ${company?.name} i dag`;
  };

  const getStatusText = (isOpen: IsOpenType): string => {
    switch (isOpen) {
      case IsOpenType.NOT_OPENED:
        return "Ikke åpnet";
      case IsOpenType.OPENED:
        return "Åpen";
      case IsOpenType.DEREGISTERING_CLOSED:
        return "Åpen, avmelding stengt";
      case IsOpenType.CLOSED:
        return "Stengt";
      default:
        return "Ukjent";
    }
  };

  const isEventInPast = () => {
    const eventDate = new Date(date);
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    eventDate.setHours(0, 0, 0, 0);
    const daysDifference = Math.ceil((eventDate.getTime() - now.getTime()) / (1000 * 3600 * 24));
    return daysDifference < 0; // I går eller lenger tilbake
  };

  return (
    <>
      <Card
        hoverable={!loading}
        onClick={loading ? undefined : openEvent}
        bodyStyle={{ padding: "0.5em", paddingLeft: "0.8em" }}
        style={{ marginBottom: isLast ? 0 : 12, opacity: loading ? 0.6 : 1 }}
        loading={loading}
      >
        <Row>
          <Col md={4} sm={6} xs={24} style={{ padding: "0.5em" }}>
            {company ? <CompanyLogo size="100%" companyId={company.id} src={company.logo} /> : <Skeleton.Image />}
          </Col>
          <Col
            md={14}
            sm={18}
            xs={24}
            style={{ paddingLeft: "1em", display: "flex", flexDirection: "column", justifyContent: "center" }}
          >
            <Row>
              <Title level={4}>{getTitle()}</Title>
            </Row>
            <Row style={{ marginTop: "5px" }}>
              <Col span={24} style={{ fontSize: "0.85em" }}>
                <Row gutter={[16, 0]} wrap={false}>
                  <Col style={{ paddingRight: "4px" }}>
                    <InfoCircleOutlined style={{ marginRight: "0.4em" }} />
                    <Text style={{ fontSize: "0.85em" }}>{getStatusText(event.is_open)}</Text>
                  </Col>
                  <Col>
                    <TeamOutlined style={{ marginRight: "0.4em" }} />
                    <Text style={{ fontSize: "0.85em" }}>
                      {event.registrations || 0}/{event.seats || 0}
                    </Text>
                  </Col>
                  <Col>
                    <UserOutlined style={{ marginRight: "0.4em" }} />
                    <Text style={{ fontSize: "0.85em" }}>
                      {companyContact ? `${companyContact.first_name} ${companyContact.last_name}` : "Ikke bestemt"}
                    </Text>
                  </Col>
                  {companyContact && (
                    <Col>
                      <PhoneOutlined style={{ marginRight: "0.4em" }} />
                      <Text style={{ fontSize: "0.85em" }}>{companyContact.telephone || "Ikke bestemt"}</Text>
                    </Col>
                  )}
                </Row>
              </Col>
            </Row>
          </Col>
          <Col
            md={6}
            sm={24}
            xs={24}
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              gap: "8px",
              paddingTop: "7px",
            }}
          >
            <Link to={`/admin/events/${event.id}/`} onClick={e => e.stopPropagation()}>
              <Button type="primary" block size="small" icon={<EyeOutlined />}>
                Vis
              </Button>
            </Link>
            {isHA && isEventInPast() ? (
              // HA med arrangement i fortiden
              <>
                {event.is_open !== IsOpenType.CLOSED ? (
                  <Button
                    danger
                    block
                    size="small"
                    icon={<LockOutlined />}
                    loading={loading}
                    disabled={loading}
                    onClick={e => {
                      e.stopPropagation();
                      handleCloseEvent();
                    }}
                  >
                    Steng
                  </Button>
                ) : event.no_shows_sent ? (
                  <Button disabled block size="small">
                    No-Show sendt
                  </Button>
                ) : (
                  <Button
                    danger
                    block
                    size="small"
                    icon={<CloseCircleOutlined />}
                    loading={loading}
                    disabled={loading}
                    onClick={e => {
                      e.stopPropagation();
                      handleSendNoShow();
                    }}
                  >
                    Send No-Show
                  </Button>
                )}
              </>
            ) : (
              // Future events or MP
              <>
                <Link to={`/admin/events/${event.id}/edit`} onClick={e => e.stopPropagation()}>
                  <Button type="default" block size="small" icon={<SettingOutlined />}>
                    Rediger
                  </Button>
                </Link>
                <Link to={`/admin/events/${event.id}/seats`} onClick={e => e.stopPropagation()}>
                  <Button type="default" block size="small" icon={<TeamOutlined />}>
                    Plasser
                  </Button>
                </Link>
              </>
            )}
          </Col>
        </Row>
      </Card>
    </>
  );
};

export default ResponsibilityBox;
